<template>
    <tr
        :data-id="appointment._id"
        :data-committed="appointment.commited"
        :class="{
            'updating-appointment': isUpdating,
            'saved-appointment': isSaved,
            'group-bg': isExpanded && isGroup,
            'selected': isSelected,
            'warning lighten-4': isClashExists || isHBAMissing
        }"
    >
        <td v-if="isPharmacy" class="ml-2">
            <v-checkbox
                @click="select"
                :value="isSelected"
                class="mt-0 ml-2"
                hide-details
            />
        </td>
        <td class="toggle-group">
            <v-icon
                v-if="isGroup && appointment.group_first"
                @click="$emit('expand', !isExpanded)"
                color="#333"
                size="20"
            >
                {{ isExpanded ? 'mdi-folder-open' : 'mdi-folder' }}
            </v-icon>
        </td>

        <td class="date" :data-date="appointment.date">
            <v-tooltip top max-width="300">
                <template v-slot:activator="{ on, attrs }">
                    <span
                        :class="appointment.commited && 'committed'"
                        v-bind="attrs"
                        v-on="appointment.commited && on"
                    >
                        {{ formatDate(appointment.date) }}
                    </span>
                </template>
                <span>Fest zugesagter Termin</span>
            </v-tooltip>
            <v-tooltip v-if="appointment.formatted_created_updated" top max-width="300">
                <template v-slot:activator="{ on, attrs }">
                    <v-icon
                        color="secondary"
                        dark
                        size="20"
                        v-bind="attrs"
                        v-on="on"
                    >
                        mdi-calendar-month
                    </v-icon>
                </template>
                <span>{{ appointment.formatted_created_updated }}</span>
            </v-tooltip>

            <v-tooltip v-if="appointment.nightshift" top max-width="300">
                <template v-slot:activator="{ on, attrs }">
                    <v-icon
                        color="secondary"
                        class="nightshift"
                        size="20"
                        v-bind="attrs"
                        v-on="on"
                    >
                        mdi-cloud
                    </v-icon>
                </template>
                <span>Nachtdienst</span>
            </v-tooltip>

            <v-tooltip v-if="appointment.self_assigned" top max-width="300">
                <template v-slot:activator="{ on, attrs }">
                    <v-icon
                        color="secondary"
                        size="20"
                        v-bind="attrs"
                        v-on="on"
                    >
                        mdi-account-check
                    </v-icon>
                </template>
                <span>Der Freiberufler hat sich diesem Termin selbst direkt zugewiesen</span>
            </v-tooltip>

            <v-tooltip v-if="appointment.formatted_pricing_suggestions" top max-width="300">
                <template v-slot:activator="{ on, attrs }">
                    <v-icon
                        color="secondary"
                        size="20"
                        class="pricing-suggestions"
                        :data-title="`Am Termin gespeicherte Bepreisung: ${appointment.formatted_pricing_suggestions}`"
                        v-bind="attrs"
                        v-on="on"
                    >
                        mdi-currency-eur
                    </v-icon>
                </template>
                <span>Am Termin gespeicherte Bepreisung: {{ appointment.formatted_pricing_suggestions }}</span>
            </v-tooltip>

            <v-tooltip v-if="appointment.allow_direct_approval==true" top max-width="300">
                <template v-slot:activator="{ on, attrs }">
                    <v-icon
                        color="green"
                        size="20"
                        class="direct-approval"
                        :data-title="`Nicht Direkt-Apotheke erlaubt direkt Zusage für diesen Termin`"
                        v-bind="attrs"
                        v-on="on"
                    >
                        mdi-alpha-d-circle
                    </v-icon>
                </template>
                <span>Nicht Direkt-Apotheke erlaubt direkt Zusage für diesen Termin</span>
            </v-tooltip>

            <v-tooltip v-if="wfState === 'deleted' && appointment.deletion_details" top max-width="300">
                <template v-slot:activator="{ on, attrs }">
                    <v-icon
                        color="secondary"
                        size="20"
                        v-bind="attrs"
                        v-on="on"
                    >
                        mdi-delete
                    </v-icon>
                </template>
                <span>{{ appointment.deletion_details }}</span>
            </v-tooltip>

            <div>
                <v-chip
                    v-for="service in appointment.display_services_with_pricing"
                    :color='getServiceColor(service.service)'
                    class="mr-1 px-2"
                    dark
                    x-small
                    label
                >
                    <v-tooltip top max-width="300">
                        <template v-slot:activator="{ on, attrs }">
                            <b
                                v-bind="attrs"
                                v-on="service.pricing_text && on"
                            >{{ service.title }}</b>
                        </template>
                        <span>{{ service.pricing_text }}</span>
                    </v-tooltip>
                </v-chip>
            </div>

            <div v-if="appointment.nonbinding">
                <v-chip
                    class="px-2"
                    color="lime darken-2"
                    tag="b"
                    x-small
                    label
                    dark
                >
                    Potenzial
                </v-chip>
            </div>

            <div v-if="appointment.hba">
                <v-chip
                    class="mt-1 px-2 hba"
                    color="purple"
                    tag="b"
                    dark
                    x-small
                    label
                    outlined
                >
                    HBA erforderlich
                </v-chip>
            </div>

            <b v-for="holiday in appointment.holidays" class="holiday">{{ holiday }}</b>
            <div>
                <assign-to-admin
                    :key="appointment._id"
                    :appointment_id="appointment._id"
                    :admin_id="appointment.admin_id"
                    @selected-user="onPharmacistSelected"
                />
                <div v-if="appointment.mep24_integration" class="mt-1 mep24-integration-row">
                    <Mep24IntegrationStatus
                        :appointmentid="appointment._id"
                        :status="appointment.mep24_integration"
                        @update="appointment => $emit('update-by-id', appointment)"
                    />
                </div>
                <cancellation-history
                    v-if="appointment.cancellation_history && appointment.cancellation_history.length>0"
                    :key="appointment._id"
                    :appointment_id="appointment._id"
                    :cancellation_history_count="appointment.cancellation_history.length"
                />
            </div>
        </td>

        <td class="group-enddate">
            <span v-if="isGroup && appointment.group_first">
                {{ formatDate(appointment.group_enddate) }}
            </span>
        </td>

        <td class="group-size">
            <span v-if="isGroup && appointment.group_first">
                ({{ appointment.group_size }})
            </span>
        </td>

        <td class="pharmacy">
            <a :href="`/pharmacies/${appointment.pharmacy_id}`">{{ appointment.pharmacy.name }}</a>
            <a :href="`/users/admin/?city=${appointment.pharmacy.zip}`" target="_blank">
                <v-icon
                    small
                    color="blue-grey"
                >
                    mdi-earth
                </v-icon>
            </a>
            <div class="text--secondary">{{ appointment.pharmacy.city }}{{", " + appointment.pharmacy.zip }}</div>
        </td>

        <td class="time">{{ appointment.display_time }}</td>

        <td>
            <div
                class="d-flex relative"
                :class="wfState === 'completed_no_apo_invoice' ? 'flex-column align-start' : 'align-center'"
            >
                <v-alert
                    v-if="isClashExists || isHBAMissing"
                    type="warning"
                    icon="mdi-alert"
                    width="300"
                    class="dates-clash"
                    dismissible
                    dense
                >
                    {{ warningMessage }}
                </v-alert>
                <div
                    :data-assignee-id="appointment.assigned_pharmacist_id || null"
                    :class="{ 'order-1': wfState === 'completed_no_apo_invoice' }"
                >
                    <PharmacistSearchDropdown
                        v-if="wfState == 'open' || wfState == 'allocated' || wfState == 'expired'"
                        class="autocomplete"
                        :services="appointment.available_services"
                        :pharmacy="appointment.pharmacy"
                        :value="selectedPharmacist"
                        @input="onPharmacistSelected"
                        :isWarning="isClashExists || isHBAMissing"
                    />
                    <v-btn
                        @click="isRemoveAssignedPharmacistDialogOpen = true"
                        v-if="(wfState === 'allocated' || wfState === 'completed_no_apo_invoice') && selectedPharmacist?._id"
                        class="d-block mt-2 unassign"
                        :class="{ 'unassign-allocated': wfState === 'allocated' }"
                        rounded
                        small
                    >
                        <v-icon left class="mr-1">mdi-cancel</v-icon>
                        FB entfernen
                    </v-btn>

                    <v-timeline
                        v-if="hasChunkPipeline && wfState == 'open'"
                        class="d-flex mt-2 ml-n4 chunks"
                        dense
                    >
                        <Chunk
                            v-for="chunk in chunkPipeline"
                            :key="chunk.chunk_index"
                            v-if="!isUpdating"
                            :index="chunk.chunk_index"
                            :status="chunk.status"
                            :time="chunk.planned_send_time"
                            :appointmentId="appointment._id"
                            @fetch-appointments="$emit('fetch-appointments')"
                        />

                        <Chunk
                            v-if="!isUpdating && appointment.chunks_data?.public_mode_time"
                            :status="publicationStatus"
                            :time="appointment.chunks_data.public_mode_time"
                            :appointmentId="appointment._id"
                            :isPublicationItem="true"
                        />
                    </v-timeline>
                </div>
                <a
                    v-if="selectedPharmacist?._id
                        && (wfState == 'open' || wfState == 'allocated')"
                    :href="`/pharmacists/${selectedPharmacist?._id}`"
                    target="_blank"
                    class="ml-3 pharmacist-link"
                >
                    <v-tooltip top max-width="300">
                        <template v-slot:activator="{ on, attrs }">
                            <v-icon
                                size="20"
                                color="blue"
                                v-bind="attrs"
                                v-on="on"
                            >
                                mdi-open-in-new
                            </v-icon>
                        </template>
                        <span>{{ selectedPharmacist.firstname }} {{ selectedPharmacist.lastname }}</span>
                    </v-tooltip>

                </a>
                <div v-if="wfState == 'completed_no_apo_invoice'
                    || wfState == 'apobilanz'
                    || wfState == 'tempInvoice'
                    || wfState == 'invoice'
                    || wfState == 'paid'"
                >
                    <a
                        :href="`/pharmacists/${selectedPharmacist?._id}`"
                        target="_blank"
                    >
                        {{ appointment.pharmacist.firstname }} {{ appointment.pharmacist.lastname }}
                    </a>
                    <v-tooltip
                        v-if="appointment.user_state == 'auto_approved'"
                        top
                        max-width="220"
                        color="#1976d2"
                    >
                        <template v-slot:activator="{ on, attrs }">
                            <v-icon
                                small
                                class="auto-approved-state"
                                aria-label="Automatisch freigegeben"
                                v-bind="attrs"
                                v-on="on"
                                color="blue darken-2"
                            >
                                mdi-checkbox-marked-circle
                            </v-icon>
                        </template>
                        <span>Automatisch freigegeben.</span>
                    </v-tooltip>
                    <v-tooltip
                        v-else-if="appointment.user_state == 'approved'"
                        top
                        max-width="170"
                        color="#00b000"
                    >
                        <template v-slot:activator="{ on, attrs }">
                            <v-icon
                                small
                                v-bind="attrs"
                                v-on="on"
                                color="green darken-2"
                            >
                                mdi-checkbox-marked-circle
                            </v-icon>
                        </template>
                        <span>Alles war ok und wie besprochen.</span>
                    </v-tooltip>
                    <v-tooltip
                        v-else-if="appointment.user_state == 'awaiting'"
                        :content-class="`awaiting-comments-tooltip-${appointment._id}`"
                        top
                        max-width="250"
                        color="#fb8c00"
                    >
                        <template v-slot:activator="{ on, attrs }">
                            <v-icon
                                small
                                v-bind="attrs"
                                v-on="on"
                                color="orange darken-2"
                            >
                                mdi-information
                            </v-icon>
                        </template>
                        <v-row class="v-application">
                            <v-col
                                cols="12"
                                class="pb-0 text-subtitle-1 font-weight-bold"
                            >
                                Es gab Änderungen:
                                <v-divider dark color="#fff"></v-divider>
                            </v-col>
                            <v-col cols="12">
                                <div v-for="awaitingComment in appointment.awaiting_comments">
                                    <b>{{ awaitingComment.label }}</b>
                                    <span>{{ awaitingComment.comment }}</span>
                                </div>
                            </v-col>
                        </v-row>
                    </v-tooltip>
                    <v-tooltip v-else top max-width="220">
                        <template v-slot:activator="{ on, attrs }">
                            <v-icon
                                small
                                v-bind="attrs"
                                v-on="on"
                            >
                                mdi-help-circle
                            </v-icon>
                        </template>
                        <span>Termin muss durch FA noch bestätigt werden.</span>
                    </v-tooltip>

                    <v-tooltip v-if="isGroup && appointment.group_first" top color="rgba(0,0,0,0.7)">
                        <template v-slot:activator="{ on, attrs }">
                            <v-icon
                                small
                                v-bind="attrs"
                                v-on="on"
                            >
                                mdi-folder-question-outline
                            </v-icon>
                        </template>
                        <div class="v-application" :style="{'display': 'block'}">
                            <section
                                v-for="(appointment, idx) in appointmentsInsideGroup"
                                :key="appointment._id"
                                :class="{'mt-3': !!idx}"
                            >
                                <span class="mr-4">{{ formatDate(appointment.date) }}</span>
                                <span v-if="appointment.user_state == 'auto_approved'">
                                    <v-icon
                                        small
                                        class="auto-approved-state"
                                        aria-label="Automatisch freigegeben"
                                        color="blue lighten-2"
                                    >
                                        mdi-checkbox-marked-circle-outline
                                    </v-icon>
                                    <span class="blue--text text--lighten-2 pb-0">
                                        Automatisch freigegeben.
                                    </span>
                                </span>
                                <span v-else-if="appointment.user_state == 'approved'">
                                    <v-icon
                                        small
                                        color="green lighten-2"
                                    >
                                        mdi-checkbox-marked-circle-outline
                                    </v-icon>
                                    <span class="green--text text--lighten-2 pb-0">
                                        Alles war ok und wie besprochen.
                                    </span>
                                </span>
                                <span v-else-if="appointment.user_state == 'awaiting'">
                                    <v-icon
                                        small
                                        color="orange lighten-2"
                                    >
                                        mdi-information-outline
                                    </v-icon>
                                    <span class="orange--text text--lighten-2 pb-0">
                                        Es gab Änderungen
                                    </span>
                                </span>
                                <span v-else>
                                    <v-icon
                                        small
                                        color="white"
                                    >
                                        mdi-help-circle-outline
                                    </v-icon>
                                    Termin muss durch FA noch bestätigt werden.
                                </span>
                            </section>
                        </div>
                    </v-tooltip>

                    <v-tooltip v-if="appointment.pharmacist.asset" top max-width="220">
                        <template v-slot:activator="{ on, attrs }">
                            <a
                                :href="`/assets/${appointment.pharmacist.asset}`"
                                target="_blank"
                            >
                                <v-icon
                                    small
                                    v-bind="attrs"
                                    v-on="on"
                                >
                                    mdi-school
                                </v-icon>
                            </a>
                        </template>
                        <span>
                            Aprobationsurkunde {{ appointment.pharmacist.firstname }} {{ appointment.pharmacist.lastname }}
                        </span>
                    </v-tooltip>
                </div>
                <AvailablePharmacistsDialogButton
                    v-if="wfState == 'inquired' || wfState == 'open'"
                    :wfState="wfState"
                    :pharmacy="appointment.pharmacy"
                    :appointment="appointment"
                    :services="appointment.available_services"
                    :date="appointment.date"
                    @selected-user="onPharmacistSelected"
                />
            </div>

        </td>


        <td class="travelcosts">
            <v-tooltip
                top
                max-width="250"
                :color="(maxCostsInfo && !isGroup)
                    ? (travelCostsIsCapped ? '#fb8c00' : '#4CAF50')
                    : '#ff5252'"
                :disabled="(appointment.travelcosts <= maxTravelCosts) && (!maxCostsInfo)"
            >
                <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                        v-if="wfState === 'open'
                            || wfState === 'allocated'
                            || wfState === 'completed_no_apo_invoice'
                            || wfState === 'expired'"
                        v-model="travelCosts"
                        :error="appointment.travelcosts > maxTravelCosts && !isGroup"
                        :class="[
                            maxCostsInfo && !isGroup
                                ? (travelCostsIsCapped ? 'warning-input' : 'success-input')
                                : ''
                        ]"
                        @input="isUpdating = true; isSaved = false"
                        type="number"
                        min="0"
                        style="min-width: 70px;"
                        hide-details
                        outlined
                        dense
                        v-bind="attrs"
                        v-on="on"
                    />
                    <span v-else>
                        {{ appointment.travelcosts ? $n(appointment.travelcosts, 'currency', 'de-DE') : '' }}
                    </span>
                </template>
                <span class="box-wrap" v-if="maxCostsInfo">{{ maxCostsInfo }}</span>
                <span v-else>Die Reisekosten sollen bei Einzelterminen nicht höher als {{ $n(maxTravelCosts, 'currency', 'de-DE') }} sein.</span>
            </v-tooltip>
        </td>
        <td class="pricing">
            <div v-for="price, i in appointment.price_details" :data-service="price.service">
                <v-tooltip right nudge-right="-30" max-width="220">
                    <template v-slot:activator="{ on, attrs }">
                        <section
                            v-if="appointment.billing.type == 'hours'"
                            :class="i == 1 && 'mt-2'"
                            v-bind="attrs"
                            v-on="on"
                        >
                            <div class="total-fp-costs">
                                <div v-if="price.has_pricing_override && price.overridden_total_apo != null">
                                    <p class="font-weight-bold">
                                        {{ $n(price.overridden_total_apo, 'currency', 'de-DE')}}
                                    </p>
                                    <p class="font-weight-bold">
                                        ({{ overrideQuantity(price) }} St. x <span class="hourly-apo-rate">{{ $n(price.overridden_apo_rate, 'currency', 'de-DE') }})</span>
                                    </p>
                                </div>
                                <div v-else>
                                    <p>
                                        {{ $n(price.total_apo, 'currency', 'de-DE')}}
                                    </p>
                                    <p>
                                        ({{ price.quantity != null ? price.quantity : appointment.quantity }} St. x <span class="hourly-apo-rate">{{ $n(price.apo_rate, 'currency', 'de-DE') }})</span>
                                    </p>
                                </div>
                            </div>
                            <div class="total-fb-costs" :class="isPricierThanFb(price) ? 'negative-balance' : 'text--secondary'">
                                <div v-if="price.has_pricing_override && price.overridden_total_fb != null">
                                    <p class="font-weight-bold">{{ $n(price.overridden_total_fb, 'currency', 'de-DE') }}</p>
                                    <p class="font-weight-bold">
                                        ({{ overrideQuantity(price) }} St. x {{ $n(price.overridden_fb_rate, 'currency', 'de-DE') }}) (FB)
                                    </p>
                                </div>
                                <div v-else>
                                    <p>{{ $n(price.total_fb, 'currency', 'de-DE') }}</p>
                                    <p>
                                        ({{ price.quantity != null ? price.quantity : appointment.quantity }} St. x {{ $n(price.fb_rate, 'currency', 'de-DE') }}) (FB)
                                    </p>
                                </div>
                            </div>
                        </section>
                        <section v-else v-bind="attrs" v-on="on">
                            <div class="total-fp-costs">
                                <p v-if="price.has_pricing_override && price.overridden_total_apo != null" class="font-weight-bold">{{ $n(price.overridden_total_apo, 'currency', 'de-DE') }}</p>
                                <p v-else>{{ $n(price.total_apo, 'currency', 'de-DE') }}</p>
                            </div>
                            <div class="total-fb-costs" :class="isPricierThanFb(price) ? 'negative-balance' : 'text--secondary'">
                                <p v-if="price.has_pricing_override && price.overridden_total_fb != null" class="font-weight-bold">{{ $n(price.overridden_total_fb, 'currency', 'de-DE') }} (FB)</p>
                                <p v-else>{{ $n(price.total_fb, 'currency', 'de-DE') }} (FB)</p>
                            </div>
                        </section>
                    </template>
                    <span>
                        {{ price.service_name }}
                    </span>
                    <span v-if="price.applied_discount && price.applied_discount.discountAmount">
                        <template v-if="price.applied_discount.discountName">
                            , Rabattname: {{ price.applied_discount.discountName }}
                        </template>, Rabattwert:  - {{ $n(price.applied_discount.discountAmount, 'currency', 'de-DE') }}
                    </span>
                    <span>
                        {{
                            price?.has_pricing_override
                                ? ", individuell gesetzter Preis"
                                : price?.is_final_price_set
                                    ? ", final gesetzter Preis"
                                    : ", Preisvorschlag oder durch das Profil überschriebener Preis"
                        }}
                    </span>
                    <span v-if="price?.has_pricing_override && price.pricing_override_audit">
                        von
                        <span v-if="price.pricing_override_audit.url">
                            {{ price.pricing_override_audit.name }}
                        </span>
                        <span v-else>{{ price.pricing_override_audit.name }}</span>
                        <span v-if="price.pricing_override_audit.date">
                            am {{ price.pricing_override_audit.date }}
                        </span>
                    </span>
                </v-tooltip>
                <div v-if="appointment.other_costs" class="other-costs">{{ $n(appointment.other_costs, 'currency', 'de-DE') }}</div>
            </div>
        </td>

        <td class="action-buttons">
            <div class="mb-2 text-center">
                <v-tooltip v-if="appointment.comment" top max-width="300">
                    <template v-slot:activator="{ on, attrs }">
                        <v-icon
                            color="secondary"
                            dark
                            size="20"
                            v-bind="attrs"
                            v-on="on"
                        >
                            mdi-comment
                        </v-icon>
                    </template>
                    <v-row class="v-application">
                        <v-col cols="12" class="pb-0 text-subtitle-1 font-weight-bold">
                            Kommentar
                            <v-divider dark color="#fff"></v-divider>
                        </v-col>
                        <v-col cols="12">
                            <span>{{ appointment.comment }}</span>
                        </v-col>
                    </v-row>
                </v-tooltip>
                <v-tooltip v-if="appointment.internal_comment" top max-width="300">
                    <template v-slot:activator="{ on, attrs }">
                        <v-icon
                            color="secondary"
                            dark
                            size="20"
                            v-bind="attrs"
                            v-on="on"
                        >
                            mdi-comment-text-multiple
                        </v-icon>
                    </template>
                    <v-row class="v-application">
                        <v-col cols="12" class="pb-0 text-subtitle-1 font-weight-bold">
                            Interner Kommentar
                            <v-divider dark color="#fff"></v-divider>
                        </v-col>
                        <v-col cols="12">
                            <span>{{ appointment.internal_comment }}</span>
                        </v-col>
                    </v-row>
                </v-tooltip>
                <v-tooltip v-if="appointment.comment_to_fb" top max-width="300">
                    <template v-slot:activator="{ on, attrs }">
                        <v-icon
                            color="secondary"
                            dark
                            size="20"
                            v-bind="attrs"
                            v-on="on"
                        >
                            mdi-comment-account-outline
                        </v-icon>
                    </template>
                    <v-row class="v-application">
                        <v-col cols="12" class="pb-0 text-subtitle-1 font-weight-bold">
                            Kommentar für Freiberufler
                            <v-divider dark color="#fff"></v-divider>
                        </v-col>
                        <v-col cols="12">
                            <span>{{ appointment.comment_to_fb }}</span>
                        </v-col>
                    </v-row>
                </v-tooltip>
            </div>

            <div v-if="!isUpdating" class="justify-center flex-wrap">
                <ProposePharmacistDialog
                    v-if="wfState !== 'inquired' && wfState !== 'deleted'"
                    :pharmacy="appointment.pharmacy"
                    :appointment="appointment"
                    :disabled="appointment.hidden"
                />
                <MarkAsHiddenBtn
                    v-if="wfState !== 'deleted'"
                    :disabled="wfState != 'open' && wfState != 'inquired'"
                    :is_appointment_hidden="appointment.hidden"
                    :appointmentid="appointment._id"
                    @update="appointments => this.$emit('update-appointments', appointments)"
                />
                <ToggleSelfAssignBtn
                    v-if="wfState !== 'deleted'"
                    :disabled="wfState != 'open' && wfState != 'inquired'"
                    :isSelfAssignDisabled="appointment.disable_self_assign"
                    :appointmentid="appointment._id"
                    @update="appointments => $emit('update-appointments', appointments)"
                />
                <ToggleDirectProposalBtn
                    v-if="wfState !== 'deleted'"
                    :disabled="wfState != 'open' && wfState != 'inquired'"
                    :isDirectProposalAllowed="appointment.allow_direct_proposal"
                    :directProposalExpiryDate="appointment.direct_proposal_expires_at"
                    :appointmentid="appointment._id"
                    @update="appointments => $emit('update-appointments', appointments)"
                />
                <ToggleApprovalAutomationBlockBtn
                    v-if="canToggleApprovalAutomation"
                    :appointmentid="appointment._id"
                    :block="appointment.approval_automation_block"
                    @update="appointment => $emit('update-by-id', appointment)"
                />
                <v-btn
                    v-if="wfState !== 'deleted'"
                    :href="`/pharmacies/${appointment.pharmacy_id}/${appointment._id}/tickets`"
                    class="tickets"
                    min-width="30"
                    small
                >
                    <v-icon small>mdi-list-box-outline</v-icon>
                </v-btn>
                <PriceSuggestion
                    v-if="wfState !== 'deleted'"
                    :pricingSuggestions="appointment.pricing_suggestions"
                ></PriceSuggestion>
                <appointment-creator
                    :appointment="appointment"
                    :pharmacy="appointment.pharmacy"
                    @fetch-appointments="$emit('fetch-appointments')"
                ></appointment-creator>
                <v-btn
                    v-if="(wfState == 'open' || wfState == 'inquired')"
                    @click="isDeleteAppointmentDialogOpen = true"
                    color="error"
                    min-width="30"
                    dark
                    small
                    class="delete-btn"
                >
                    <v-icon small>mdi-trash-can-outline</v-icon>
                </v-btn>
                <AutoProposalBtn
                    v-if="wfState === 'open'"
                    :disabled="appointment.hidden || appointment.allow_direct_proposal"
                    :appointment_id="appointment._id"
                    :is_chunk_data="hasChunkPipeline"
                    :is_auto_proposed="appointment.is_auto_proposed"
                    @update="appointments => $emit('update-appointments', appointments)"
                />
                <v-btn
                    @click="commit"
                    v-if="!appointment.commited
                        && (appointment.group_first || !isGroup)
                        && wfState == 'open'"
                    :loading="isLoading"
                    color="primary"
                    class="commit-with-email"
                    small
                    outlined
                >
                    Zusage an Apotheke
                </v-btn>
                <v-btn
                    v-if="wfState == 'open' && hasChunkPipeline && publicationStatus !== 'sent'"
                    @click="setLive"
                    color="success"
                    rounded
                    small
                >
                    SET Publik
                </v-btn>

                <ApprovalReminderDialogButton
                    v-if="wfState == 'completed_no_apo_invoice' && (appointment.user_state === '' || appointment.user_state === null || appointment.user_state === 'open')"
                    v-model="isApprovalReminderDialogOpen"
                    :pharmacist_id="appointment.assigned_pharmacist_id"
                />


                <PharmacistsRequests
                    v-if="(wfState == 'open' || wfState == 'allocated' || wfState == 'deleted')
                        && (!isGroup || appointment.group_first)"
                    :appointment="appointment"
                    :pharmacy="appointment.pharmacy"
                    :assignedPharmacist="appointment.pharmacist"
                    :knownPharmacists="appointment.known_pharmacists"
                    :numOfRequests="appointment.pharmacist_requests_count"
                    @selected-user="onPharmacistSelected"
                />
            </div>

            <div v-else class="text-center">
                <v-tooltip
                    v-if="wfState == 'open'
                        || wfState == 'allocated'
                        || wfState === 'completed_no_apo_invoice'
                        || wfState === 'expired'"
                    top
                    max-width="400"
                >
                    <template v-slot:activator="{ on, attrs }">
                        <v-btn
                            @click="onSaveClick('SAVE')"
                            :loading="isLoading && savingAction === 'SAVE'"
                            color="cyan lighten-1"
                            min-width="70"
                            class="save"
                            small
                            dark
                            v-bind="attrs"
                            v-on="on"
                        >
                            Speichern
                        </v-btn>
                    </template>
                    <span>Speichern, es werden keine E-Mails versendet</span>
                </v-tooltip>
                <v-tooltip
                    v-if="(!isGroup || appointment.group_first)
                        && (wfState == 'open' || wfState == 'expired')
                        && selectedPharmacist?._id"
                    max-width="400"
                    top
                >
                    <template v-slot:activator="{ on, attrs }">
                        <v-btn
                            @click="onSaveClick('SAVE_WITH_MAIL')"
                            :loading="isLoading && savingAction === 'SAVE_WITH_MAIL'"
                            color="cyan lighten-1"
                            min-width="70"
                            class="mt-2 save-with-email"
                            small
                            dark
                            v-bind="attrs"
                            v-on="on"
                        >
                            Speichern + E-Mails senden
                        </v-btn>
                    </template>
                    <span>Speichern und E-Mails senden, aber ohne Unterkunftsemail</span>
                </v-tooltip>
                <v-tooltip v-if="appointment.group_first && appointment.is_part_of_a_blockbuster && selectedPharmacist?._id" top max-width="400">
                    <template v-slot:activator="{ on, attrs }">
                        <v-btn
                            @click="onSaveClick('SAVE_WITH_ACCOM_MAIL')"
                            :loading="isLoading && savingAction === 'SAVE_WITH_ACCOM_MAIL'"
                            color="cyan lighten-1"
                            min-width="70"
                            class="mt-2 save-with-accommodation"
                            small
                            dark
                            v-bind="attrs"
                            v-on="on"
                        >
                            S + E + Unterkunft
                        </v-btn>
                    </template>
                    <span>Speichern und E-Mails inklusive Unterkunftsemail senden</span>
                </v-tooltip>
            </div>
        </td>

        <DeleteAppointmentDialog
            v-model="isDeleteAppointmentDialogOpen"
            @delete="payload => $emit('delete', payload)"
        />
        <ConfirmationDialog
            v-model="isCommittedDialogOpen"
            message="Achtung: Der Termin wurde noch nicht fest zugesagt! Wollen Sie ihn wirklich speichern?"
            @save="handleSavingProcess"
        />
        <ConfirmationDialog
            v-model="isReassignmentConflictDialogOpen"
            :message="reassignmentConflictMessage"
            @save="confirmReassignment"
        >
            <v-checkbox
                v-model="sendReassignmentCancellationMail"
                class="send-reassignment-cancellation-mail mt-2"
                label="Storno-Mail an den aktuell zugewiesenen Freiberufler senden"
                hide-details
            />
        </ConfirmationDialog>
        <AcceptanceCommentDialog
            v-model="isAcceptanceCommentDialogOpen"
            @save="saveAppointment"
        />
        <UnssignPharmacistDialog
            v-model="isRemoveAssignedPharmacistDialogOpen"
            @save="unassignPharmacist"
        />
    </tr>
</template>

<script>
    import axios from 'axios';
    import _ from 'lodash';
    import moment from 'moment';
    import { mapMutations } from 'vuex';

    import AppointmentCreator from './AppointmentCreator.vue';
    import AvailablePharmacistsDialogButton from './AvailablePharmacistsDialogButton.vue';
    import MarkAsHiddenBtn from './MarkAsHiddenBtn.vue';
    import DeleteAppointmentDialog from './DeleteAppointmentDialog.vue';
    import ApprovalReminderDialogButton from './ApprovalReminderDialogButton.vue';
    import ConfirmationDialog from './ConfirmationDialog.vue';
    import ProposePharmacistDialog from './ProposePharmacistDialog.vue';
    import PharmacistSearchDropdown from '../pharmacist/PharmacistSearchDropdown.vue';
    import PharmacistsRequests from '../pharmacist/requests/PharmacistsRequests.vue';
    import ToggleSelfAssignBtn from './ToggleSelfAssignBtn.vue';
    import ToggleDirectProposalBtn from './ToggleDirectProposalBtn.vue';
    import ToggleApprovalAutomationBlockBtn from './ToggleApprovalAutomationBlockBtn.vue';
    import Mep24IntegrationStatus from './Mep24IntegrationStatus.vue';
    import PriceSuggestion from './PriceSuggestion.vue';
    import CancellationHistory from './CancellationHistory.vue';
    import AutoProposalBtn from './AutoProposalBtn.vue';
    import AcceptanceCommentDialog from './AcceptanceCommentDialog.vue';
    import UnssignPharmacistDialog from './UnssignPharmacistDialog.vue';
    import Chunk from './Chunk.vue';

    const SAVE_URL_PATH = {
        'SAVE': 'update',
        'SAVE_WITH_MAIL': 'update_ext/false',
        'SAVE_WITH_ACCOM_MAIL': 'update_ext/true'
    }

    export default {
        components: {
            AppointmentCreator,
            AvailablePharmacistsDialogButton,
            DeleteAppointmentDialog,
            ConfirmationDialog,
            ProposePharmacistDialog,
            PharmacistSearchDropdown,
            MarkAsHiddenBtn,
            PharmacistsRequests,
            ToggleSelfAssignBtn,
            ToggleDirectProposalBtn,
            ToggleApprovalAutomationBlockBtn,
            Mep24IntegrationStatus,
            PriceSuggestion,
            ApprovalReminderDialogButton,
            CancellationHistory,
            AutoProposalBtn,
            AcceptanceCommentDialog,
            UnssignPharmacistDialog,
            Chunk
        },
        props: {
            appointment: Object,
            wfState: String,
            expand: Function,
            isExpanded: Boolean,
            isSelected: Boolean,
            isPharmacy: Boolean,
            appointmentsInsideGroup: Array
        },
        data() {
            return {
                isAvailablepharmacistsDialogOpen: false,
                isDeleteAppointmentDialogOpen: false,
                isCommittedDialogOpen: false,
                isReassignmentConflictDialogOpen: false,
                isAcceptanceCommentDialogOpen: false,
                isRemoveAssignedPharmacistDialogOpen: false,
                selectedPharmacist: this.appointment.assigned_pharmacist,
                isLoading: false,
                isUpdating: false,
                isSaved: false,
                isClashExists: false,
                isHBAMissing: false,
                maxCostsInfo: '',
                savingAction: '', // SAVE || SAVE_WITH_MAIL || SAVE_WITH_ACCOM_MAIL
                travelCostsIsCapped: false,
                unassignPharmacistData: {},
                assignmentConflict: null,
                sendReassignmentCancellationMail: false,
                pendingAssignmentComment: undefined
            }
        },
        methods: {
            formatDate(date) {
                if (!date) return ''
                return new Date(date).toLocaleDateString('de-DE', {
                    weekday: 'short',
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                })
            },
            momentFormat(unformattedDate) {
                let date = unformattedDate;
                if (!date.includes('+')) date += 'Z'; // for date strings without Z
                return moment(date).format('DD-MM-YYYY, HH:mm');
            },
            async onPharmacistSelected(pharmacist) {
                if (!pharmacist) return
                this.selectedPharmacist = pharmacist;
                const pharmacyId = this.appointment.pharmacy_id;
                const appointmentId = this.appointment._id;

                await this.getTravelCosts(this.appointment._id, pharmacist._id);
                const status = await this.checkDates(pharmacyId, appointmentId);
                if (status === 'wait') this.isClashExists = true
                else if (this.appointment.hba && !pharmacist.has_hba) {
                    this.isHBAMissing = true
                }
                else {
                    this.isClashExists = false;
                    this.isHBAMissing = false;
                }
            },

            async getTravelCosts(appointmentId, pharmacistId) {
                try {
                    const response = await axios.get(`/api/appointments/travelcosts`, { params: {
                        appointment_id: appointmentId,
                        pharmacist_id: pharmacistId
                    }});
                    const routeCosts = Array.isArray(response.data) ? response.data : [];
                    const preview = routeCosts[0];
                    if (!preview) {
                        this.travelCostsIsCapped = false;
                        this.maxCostsInfo = '';
                        this.isUpdating = true;
                        this.isSaved = false;
                        return;
                    }

                    this.travelCostsIsCapped = !!preview.is_capped;
                    this.maxCostsInfo = this.buildTravelCostInfo(preview);
                    this.isUpdating = true;
                    this.isSaved = false;

                    routeCosts.forEach(apt => {
                        this.$emit('update-by-id', {
                            _id: apt.appointment_id,
                            travelcosts: apt.costs
                        })
                    })

                } catch (error) {
                    this.alertError(error)
                }
            },
            formatTravelCostNumber(value, decimals = 0, suffix = '') {
                const numeric = Number(value);
                if (!Number.isFinite(numeric)) return '-';
                return `${numeric.toFixed(decimals)}${suffix}`;
            },
            formatTravelCostCurrency(value) {
                const numeric = Number(value);
                if (!Number.isFinite(numeric)) return '-';
                const rendered = Number.isInteger(numeric) ? String(numeric) : numeric.toFixed(2);
                return `${rendered}€`;
            },
            buildTravelCostInfo(preview) {
                const costModel = preview.has_blockbuster_travel_cost ? "Blockbuster" : "pro Einzelfahrt";
                const calculatedCosts = this.formatTravelCostCurrency(preview.costs);
                const nonCappedCosts = this.formatTravelCostCurrency(preview.non_capped_costs);
                const maxTravelCost = this.formatTravelCostCurrency(preview.pharmacy_max_travel_cost);
                const distance = this.formatTravelCostNumber(preview.distance, 0, ' km');
                const straightLineDistance = this.formatTravelCostNumber(preview.straight_line_distance, 0, 'km');

                return `Berechnete Kosten: ${calculatedCosts} (${costModel})
                            FB Kosten: ${nonCappedCosts} (${distance})
                            Max. Kosten: ${maxTravelCost} (${costModel})
                            Luftlinie: ${straightLineDistance}
                            Die mindest Kosten liegen bei 20€ pro Fahrt.`;
            },

            async checkDates(pharmacyId, appointmentId) {
                if (!this.selectedPharmacist) return
                try {
                    const response = await axios.post(`/pharmacies/${pharmacyId}/check_date`, {
                        a_id: appointmentId,
                        pharmacist_id: this.selectedPharmacist._id
                    })
                    return response.data.status
                } catch (error) {
                    this.alertError(error)
                }
            },

            handleSavingProcess() {
                // decide between direct save or enter a comment and save
                this.savingAction === 'SAVE'
                    ? this.saveAppointment()
                    : this.isAcceptanceCommentDialogOpen = true
            },

            onSaveClick(action) {
                this.savingAction = action;
                // should confirm before saving uncomitted appointments
                !this.appointment.commited
                    ? this.isCommittedDialogOpen = true
                    : this.handleSavingProcess()
            },

            async saveAppointment(comment, reassignment = {}) {
                this.pendingAssignmentComment = comment;
                const { send_mail, remove_ma_from_appointment, is_cancelled_by_pharmacist } = this.unassignPharmacistData;
                const path = SAVE_URL_PATH[this.savingAction];
                const appointmentId = this.appointment._id;
                const pharmacyId = this.appointment.pharmacy_id;
                const service = this.selectedPharmacist?.display_services_full ? this.selectedPharmacist.display_services_full[0].service : this.selectedPharmacist?.services[0];
                let travelcostsByAppointmentId = `${appointmentId}=${this.travelCosts}`;
                if (this.isGroup && this.appointment.group_first && this.appointmentsInsideGroup) {
                    this.appointmentsInsideGroup.forEach(appointment => {
                        travelcostsByAppointmentId = travelcostsByAppointmentId.concat(',', `${appointment._id}=${appointment.travelcosts}`);
                    })
                }
                const requestBody = {
                    pharmacist_id: this.selectedPharmacist?._id || '__unset__',
                    service,
                    travelcosts: this.travelCosts,
                    travelcostsByAppointmentId: travelcostsByAppointmentId,
                    ...(comment && { comment }),
                    ...(send_mail && { send_mail }),
                    ...(remove_ma_from_appointment && { remove_ma_from_appointment }),
                    ...(is_cancelled_by_pharmacist && { is_cancelled_by_pharmacist }),
                    ...(reassignment.force && {
                        force_reassignment: true,
                        expected_current_pharmacist_id: this.assignmentConflict?.assigned_pharmacist?._id,
                        send_reassignment_cancellation_mail: this.sendReassignmentCancellationMail,
                    }),
                };
                this.isLoading = true

                try {
                    const response = await axios.post(`/pharmacies/${pharmacyId}/${appointmentId}/${path}`, requestBody);
                    if (response.data.status !== 'ok') throw new Error()
                    if (requestBody.pharmacist_id === '__unset__') {
                        this.$emit('update-by-id', {
                            _id: appointmentId,
                            allow_direct_proposal: false,
                            direct_proposal_expires_at: null,
                            auto_proposal_after_timer: false,
                        });
                        this.$emit('fetch-appointments');
                    }
                    this.isUpdating = false;
                    this.isSaved = true;
                    this.alertSuccess('erfolgreich gespeichert')

                } catch (error) {
                    if (
                        error.response?.status === 409
                        && error.response?.data?.code === 'assignment_conflict'
                    ) {
                        this.assignmentConflict = error.response.data;
                        this.sendReassignmentCancellationMail = false;
                        this.isReassignmentConflictDialogOpen = true;
                        return;
                    }
                    this.alertError(error.response?.data?.message)
                } finally {
                    this.isLoading = false
                }
            },
            confirmReassignment() {
                return this.saveAppointment(this.pendingAssignmentComment, { force: true });
            },
            getServiceColor(service='substitution') {
                const servicesColors = {
                    substitution: 'cyan',
                    pta: 'warning',
                    pka: 'success',
                    qms: 'red',
                    storno: 'red'
                }
                return servicesColors[service]
            },
            getChunkStatusColor(status) {
                const chunkColors = {
                    skipped_empty: 'red',
                    scheduled: 'orange',
                    next: 'yellow',
                    sent: 'green'
                }
                return chunkColors[status] || 'grey'
            },
            getChunkStatusName(status) {
                const chunkColors = {
                    skipped_empty: 'Übersprungen',
                    scheduled: 'Geplant',
                    next: 'Als nächstes',
                    sent: 'Gesendet'
                }
                return chunkColors[status] || 'Ausstehend'
            },
            async commit() {
                const appointmentId = this.appointment._id;
                const pharmacyId = this.appointment.pharmacy_id;
                this.isLoading = true;
                try {
                    const response = await axios.post(`/pharmacies/${pharmacyId}/${appointmentId}/commit_with_mail_ticket`);
                    const appointments = response.data;
                    this.$emit('update-appointments', appointments);
                    this.alertSuccess('Termine zugesagt')
                } catch (error) {
                    this.alertError()
                } finally {
                    this.isLoading = false
                }
            },
            unassignPharmacist(unassignmentData) {
                this.selectedPharmacist = null;
                this.isUpdating = true;
                this.unassignPharmacistData = unassignmentData
            },
            async select() {
                if (this.isGroup && this.appointment.group_first && !this.isExpanded) {
                    await this.$emit('expand', true);
                    this.$emit('select-group', !this.isSelected);
                } else {
                    this.$emit('select', !this.isSelected);
                }
            },
            async setLive() {
                try {
                    const response = await axios.post(`/api/appointments/force_chunks/${this.appointment._id}`);
                    if (response.status > 400) throw new Error();
                    this.$emit('update-appointments', response.data)
                    this.alertSuccess('Termine ist Publik');
                } catch (error) {
                    this.alertError()
                }
            },
            isPricierThanFb(price){
                if(price.has_pricing_override && price.overridden_total_fb != null && price.overridden_total_apo != null){
                    return price.is_overridden_fb_pricier_than_apo;
                }
                return price.is_fb_pricier_than_apo
            },
            overrideQuantity(price) {
                if(price.overridden_quantity != null) return price.overridden_quantity;
                if(price.quantity != null) return price.quantity;
                return this.appointment.quantity;
            },
            ...mapMutations({
                alertError: 'alert/error',
                alertSuccess: 'alert/success',
            })
        },
        computed: {
            reassignmentConflictMessage() {
                const current = this.assignmentConflict?.assigned_pharmacist || {};
                const currentName = [current.firstname, current.lastname].filter(Boolean).join(' ')
                    || 'einem anderen Freiberufler';
                const selectedName = [this.selectedPharmacist?.firstname, this.selectedPharmacist?.lastname]
                    .filter(Boolean)
                    .join(' ')
                    || 'dem ausgewählten Freiberufler';
                return `Der Termin ist aktuell bereits ${currentName} zugewiesen. Soll er trotzdem ${selectedName} zugewiesen werden?`;
            },
            canToggleApprovalAutomation() {
                const unavailableStates = ['apobilanz', 'tempinvoice', 'invoice', 'paid'];
                return !unavailableStates.includes(String(this.wfState || '').toLowerCase());
            },
            travelCosts: {
                get() {
                    return this.appointment.travelcosts || 0
                },
                set(val) {
                    this.$emit('update-by-id', {
                        _id: this.appointment._id,
                        travelcosts: val
                    })
                },
            },
            maxTravelCosts() {
                return this.appointment.pharmacy.max_travel_cost || 80
            },
            chunkPipeline() {
                const chunks = this.appointment.chunks_data?.chunks;
                if (!Array.isArray(chunks)) {
                    return [];
                }
                return chunks.filter(chunk => chunk?.chunk_index != null && chunk?.status && chunk?.planned_send_time);
            },
            hasChunkPipeline() {
                if (Object.prototype.hasOwnProperty.call(this.appointment, 'has_proposal_chunk_pipeline')) {
                    return this.appointment.has_proposal_chunk_pipeline === true && this.chunkPipeline.length > 0;
                }
                return this.chunkPipeline.length > 0;
            },
            isPublicationTimeReached() {
                const publicationTime = this.appointment.chunks_data?.public_mode_time;
                if (!publicationTime) {
                    return false;
                }
                const parsedPublicationTime = typeof publicationTime === 'string'
                    && !/[zZ]|[+-]\d\d:?\d\d$/.test(publicationTime)
                    ? `${publicationTime}Z`
                    : publicationTime;
                return moment(parsedPublicationTime).isSameOrBefore(moment());
            },
            publicationStatus() {
                if (!this.hasChunkPipeline) {
                    return undefined;
                }
                const lastChunk = this.chunkPipeline[this.chunkPipeline.length - 1];
                if (!this.appointment.allow_direct_proposal) {
                    return 'sent'
                } else if (lastChunk?.status == 'sent' && !this.isPublicationTimeReached) {
                    return 'next'
                }
                else return
            },
            isGroup() {
                return +this.appointment.group_size > 1
            },
            warningMessage() {
                return this.isClashExists
                    ? 'Es gibt Terminkollisionen'
                    : this.isHBAMissing ? 'Für diesen Apotheker fehlt HBA' : ''
            }
        },
    }
</script>

<style scoped>
    td {
        padding: 8px !important;
    }
    p {
        margin: 0 !important;
    }
    tr {
        transition: all 0.3s ease;
    }
    .group-bg {
        background-color: rgba(239, 239, 239, 0.537)
    }
    .selected {
        background-color: rgba(0, 128, 255, 0.093) !important;
    }
    .fb-button {
        padding: 0 !important;
        margin-left: 10px;
    }
    .updating-appointment, .updating-appointment:hover {
        background: #ffffe0 !important;
    }
    .saved-appointment, .saved-appointment:hover {
        background: #e0ffe0 !important
    }
    .holiday {
        color: maroon;
        margin-top: 7px;
    }
    .autocomplete {
        width: 150px;
    }
    .toggle-group { width: 36px; }
    .date { width: 200px; }
    .group-enddate { width: 100px; }
    .group-size { width: 50px; }
    .pharmacy { width: 166px; }
    .time { width: 160px; }
    .group-pharmacist { width: 222px; }
    .pricing { width: 170px; }
    .action-buttons { width: 170px; }
    .flex-wrap {
        display: flex;
        gap: 8px;
        flex-wrap: wrap;
    }
    .commit-with-email {
        font-size: 12px !important;
        padding-inline: 7px !important;
    }
    .negative-balance {
        color: red;
    }
    .relative {
        position: relative;
    }
    .dates-clash {
        position: absolute;
        bottom: 30px;
        right: 0;
    }
    .unassign-allocated, .chunks {
        position: absolute;
    }
    .vuetify-wrapper .theme--light.v-timeline:before {
        display: none;
    }
</style>
<style>
    .committed {
        background-color: #FCFB93;
    }
    .warning-input fieldset {
        color: #fb8c00 !important;
        caret-color: #fb8c00 !important;
        border: 2px solid !important;
        white-space: pre-line;
    }
    .success-input fieldset {
        color: #4CAF50 !important;
        caret-color: #4CAF50 !important;
        border: 2px solid !important;
        white-space: pre-line;

    }
    .box-wrap {
        white-space: pre-line;
    }
    .vuetify-wrapper .chunks .v-timeline-item__divider {
        min-width: 1.9rem
    }
    .vuetify-wrapper .chunks .v-timeline-item__dot {
        z-index: 0;
        cursor: pointer;
        transition: all 0.3s ease;
    }
    .vuetify-wrapper .chunks .numeric-chunk .v-timeline-item__dot--small {
        width: 20px;
        height: 20px;
        margin-top: 0.12rem;
    }

    .vuetify-wrapper .chunks .green-shadow:not(.numeric-chunk) .v-timeline-item__dot--small::after {
        content: '';
        display: inline-block;
        position: absolute;
        top: 0;
        border-radius: 50%;
        width: inherit;
        height: inherit;
        margin: inherit;
        animation: pulse 2s linear infinite;
    }

    .vuetify-wrapper .chunks .green-shadow .v-timeline-item__dot {
        box-shadow: 0px 0px 2px 4px #c3e6cd;
    }
    .vuetify-wrapper .chunks .green-shadow .v-timeline-item__dot:hover {
        box-shadow: 0px 0px 2px 4.5px #a5e1b6;
    }
    .vuetify-wrapper .chunks .red-shadow .v-timeline-item__dot {
        box-shadow: 0px 0px 2px 4px #e6c3c3;
    }
    .vuetify-wrapper .chunks .red-shadow .v-timeline-item__dot:hover {
        box-shadow: 0px 0px 2px 4.5px #e4bdbd;
    }
    .vuetify-wrapper .chunks .orange-shadow .v-timeline-item__dot {
        box-shadow: 0px 0px 2px 4px #f2dec4;
    }
    .vuetify-wrapper .chunks .orange-shadow .v-timeline-item__dot:hover {
        box-shadow: 0px 0px 2px 4.5px #f2d6b2;
    }
    .vuetify-wrapper .chunks .grey-shadow .v-timeline-item__dot {
        box-shadow: 0px 0px 2px 4px #e6e6e6;
    }
    @keyframes pulse {
        0% {
            box-shadow: 0 0 0 0 rgb(44 204 67 / 40%)
        }
        50% {
            box-shadow: 0 0 0 11px rgba(204,169,44, 0);
        }
        100% {
            box-shadow: 0 0 0 0 rgba(204,169,44, 0);
        }
    }
</style>
